import { Link } from 'react-router-dom';

const faqItems = [
  {
    question: 'What is a CSSBuy spreadsheet?',
    answer: 'A CSSBuy spreadsheet is a curated list of product links organized in a spreadsheet format, typically shared within the rep and streetwear community. These spreadsheets contain direct links to items available on platforms like Taobao, Weidian, and 1688 that can be purchased through the CSSBuy agent service. Each spreadsheet entry usually includes product names, prices, seller ratings, QC photos references, and size guides — making it significantly easier to find high-quality items without endless manual searching.',
  },
  {
    question: 'How often are the spreadsheets updated?',
    answer: 'Our CSSBuy spreadsheet directory is updated on a weekly basis. Every Friday, our team reviews new submissions, verifies existing links, and adds fresh finds across all 14 categories. We also perform monthly quality audits to remove dead links, update pricing information, and ensure every spreadsheet in our directory meets our quality standards. For the most current CSSBuy spreadsheet 2026 collection, bookmark this page and check back regularly.',
  },
  {
    question: 'How do I use a CSSBuy spreadsheet to place an order?',
    answer: 'Using a CSS spreadsheet is straightforward. First, browse our directory and find a category or product you are interested in. Click through to the main store where you will find the complete spreadsheet with product links. Copy the product link you want, paste it into your CSSBuy agent\'s search bar, and the agent will handle the purchasing process — including QC photos, warehouse storage, and international shipping. For a detailed walkthrough, check out our <a href="/cssbuy-spreadsheet-guide-2026" className="text-primary-600 hover:text-primary-700 underline">complete CSSBuy spreadsheet guide</a>.',
  },
  {
    question: 'Are these spreadsheets free to access?',
    answer: 'Yes, all CSSBuy spreadsheets listed in our directory are completely free to access and use. We believe in making quality shopping resources available to everyone in the community. The spreadsheets link to products on various Chinese e-commerce platforms, and you only pay for the items you choose to purchase along with standard agent fees and shipping costs through CSSBuy. There are no hidden subscription fees or paywalls on our directory.',
  },
  {
    question: 'How do you verify the quality of products in the spreadsheets?',
    answer: 'Our verification process involves multiple steps. First, we check seller ratings and transaction history on the original platform. Second, we review community feedback and QC photos shared by previous buyers. Third, we monitor return rates and dispute records. Only spreadsheets with consistently positive feedback and reliable sellers make it into our directory. We also maintain a blacklist of unreliable sellers to protect our users from scams and low-quality products.',
  },
  {
    question: 'Can I submit my own spreadsheet to be listed?',
    answer: 'We welcome community contributions. If you have created a quality CSSBuy spreadsheet that you would like to share, you can reach out through our main store contact page. Our team will review your submission for accuracy, link validity, and content quality. Approved spreadsheets will be added to the appropriate category with credit given to the original creator. Please ensure your spreadsheet follows our community guidelines before submitting.',
  },
];

export default function FAQSection() {
  return (
    <section id="faq" className="py-20 md:py-28 bg-background-50">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary-500 mb-3">
            FAQ
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-foreground-600 text-sm md:text-base">
            Everything you need to know about CSSBuy spreadsheets, how they work, and how to get the most out of our directory.
          </p>
        </div>

        <div className="space-y-4">
          {faqItems.map((item, index) => (
            <details
              key={index}
              className="group bg-white border border-background-200/70 rounded-2xl overflow-hidden"
            >
              <summary className="flex items-center justify-between px-6 py-5 cursor-pointer select-none">
                <span className="font-heading text-base md:text-lg font-semibold text-foreground-900 pr-4">
                  {item.question}
                </span>
                <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-background-100 group-open:bg-primary-100 transition-colors duration-200">
                  <i className="ri-add-line text-foreground-600 text-sm group-open:hidden"></i>
                  <i className="ri-subtract-line text-primary-600 text-sm hidden group-open:block"></i>
                </div>
              </summary>
              <div className="px-6 pb-5 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p dangerouslySetInnerHTML={{ __html: item.answer }}></p>
              </div>
            </details>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/cssbuy-spreadsheet-guide-2026"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap"
          >
            Read the complete guide for more detailed answers
            <i className="ri-arrow-right-line ml-1.5"></i>
          </Link>
        </div>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer.replace(/<[^>]*>/g, ''),
              },
            })),
          }),
        }}
      />
    </section>
  );
}